import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { sendApiRequest } from 'lib/api/public';
import notify from 'lib/notify';
import Link from 'components/common/Link';
import Modal from 'components/common/Modal';
import { sanitizeHtml } from 'lib/sanitizeHtml';
import {
  SYSTEM_NOTIFICATION_HREFS,
  SYSTEM_NOTIFICATION_TYPES,
  buildSystemNotificationContent,
  isFlagSystemNotificationType,
  isSystemNotificationType,
} from 'components/notifications/systemNotifications';

type FailedSequenceSummary = {
  sequenceId: string;
  sequenceName: string;
  candidates: { candidateId: string; candidateName: string }[];
  moreCandidatesCount: number;
};

export type NotificationItem = {
  _id: string;
  userId: string;
  type: string;
  content: string;
  data?: {
    sequenceId?: string;
    sequenceName?: string;
    candidateId?: string;
    candidateName?: string;
    count?: number;
    href?: string;
    [key: string]: unknown;
  };
  createdAt: string;
  viewedAt?: string;
  level?: 'info' | 'warning';
};

export type NotificationsResponse = {
  list: NotificationItem[];
  notifications?: NotificationItem[];
  totalCount: number;
  unreadCount: number;
};

export const NOTIFICATIONS_UNREAD_EVENT = 'harbor-notifications-unread';

export const publishNotificationsUnreadCount = (unreadCount: number) => {
  window.dispatchEvent(
    new CustomEvent(NOTIFICATIONS_UNREAD_EVENT, {
      detail: { unreadCount },
    }),
  );
};

const notificationLinkSx = (theme: Theme) => ({
  color: theme.palette.text.primary,
  textDecoration: 'underline',
  textDecorationColor: theme.palette.text.primary,
  textUnderlineOffset: '2px',
  '&&, &&:hover, &&:visited, &&:active': {
    color: `${theme.palette.text.primary} !important`,
    textDecorationColor: theme.palette.text.primary,
  },
});

export const notificationContentSx = (isUnread: boolean) => ({
  fontWeight: isUnread ? 600 : 400,
  lineHeight: 1.35,
  color: 'text.primary',
  opacity: isUnread ? 1 : 0.65,
});

const linkNotification = (href: string, children: React.ReactNode) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" sx={notificationLinkSx}>
    {children}
  </Link>
);

export const renderNotificationContent = (notification: NotificationItem) => {
  if (
    notification.type === 'note-mention' ||
    notification.type === 'task-mention' ||
    notification.type === 'task-assigned' ||
    notification.type === 'demo-call-assigned'
  ) {
    const href = typeof notification.data?.href === 'string' ? notification.data.href : null;

    if (!href) {
      return <Box component="span">{notification.content}</Box>;
    }

    return linkNotification(href, notification.content);
  }

  if (notification.type === 'new-device-login') {
    const content = notification.content || '';
    const settingsLabel = 'settings';
    const settingsIndex = content.lastIndexOf(settingsLabel);

    if (settingsIndex === -1) {
      return <Box component="span">{content}</Box>;
    }

    return (
      <Box component="span">
        {content.slice(0, settingsIndex)}
        {linkNotification('/settings/account?highlight=devices', settingsLabel)}
        {content.slice(settingsIndex + settingsLabel.length)}
      </Box>
    );
  }

  if (notification.type === 'sequence-email-failed' && notification.data?.sequenceId) {
    const sequenceName = notification.data.sequenceName || 'Sequence';
    const candidateName = notification.data.candidateName || 'a candidate';
    const sequenceId = notification.data.sequenceId;
    const candidateId = notification.data.candidateId;
    const recipientsHref = candidateId
      ? `/sequences/${encodeURIComponent(sequenceId)}?tab=1&candidateId=${encodeURIComponent(candidateId)}`
      : `/sequences/${encodeURIComponent(sequenceId)}?tab=1`;

    return linkNotification(recipientsHref, `${sequenceName} has failed to send to ${candidateName}.`);
  }

  if (
    notification.type === SYSTEM_NOTIFICATION_TYPES.FAILED_SEQUENCE_EMAILS &&
    Array.isArray(notification.data?.failedSequences) &&
    notification.data.failedSequences.length > 0
  ) {
    const failedSequences = notification.data.failedSequences as FailedSequenceSummary[];

    return (
      <Box>
        {buildSystemNotificationContent(
          notification.type,
          typeof notification.data?.count === 'number' ? notification.data.count : 0,
        )}
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {failedSequences.map(sequence => {
            const sequenceHref = `/sequences/${encodeURIComponent(sequence.sequenceId)}?tab=1`;

            return (
              <li key={sequence.sequenceId}>
                {linkNotification(sequenceHref, sequence.sequenceName)}
                <Box component="ul" sx={{ m: 0, pl: 2, fontWeight: 400 }}>
                  {sequence.candidates.map(candidate => (
                    <li key={candidate.candidateId}>
                      {linkNotification(
                        `${sequenceHref}&candidateId=${encodeURIComponent(candidate.candidateId)}`,
                        candidate.candidateName,
                      )}
                    </li>
                  ))}
                  {sequence.moreCandidatesCount > 0 && <li>and {sequence.moreCandidatesCount} more</li>}
                </Box>
              </li>
            );
          })}
        </Box>
      </Box>
    );
  }

  if (isSystemNotificationType(notification.type)) {
    if (isFlagSystemNotificationType(notification.type)) {
      const content = buildSystemNotificationContent(notification.type);
      const billingLabel = 'billing';
      const billingIndex = content.lastIndexOf(billingLabel);

      if (billingIndex === -1) {
        return <Box component="span">{content}</Box>;
      }

      return (
        <Box component="span">
          {content.slice(0, billingIndex)}
          {linkNotification('/settings/billing', billingLabel)}
          {content.slice(billingIndex + billingLabel.length)}
        </Box>
      );
    }

    const href = SYSTEM_NOTIFICATION_HREFS[notification.type];
    const content = buildSystemNotificationContent(
      notification.type,
      typeof notification.data?.count === 'number' ? notification.data.count : 0,
    );

    if (!href) {
      return <Box component="span">{content}</Box>;
    }

    return linkNotification(href, content);
  }

  // Legacy system digests stored href on the server before copy/links moved client-side.
  const href = typeof notification.data?.href === 'string' ? notification.data.href : null;
  if (href) {
    return linkNotification(href, notification.content);
  }

  return (
    <Box
      component="span"
      sx={theme => ({
        '& a, & a:hover, & a:visited, & a:active': {
          color: `${theme.palette.text.primary} !important`,
          textDecoration: 'underline',
          textDecorationColor: theme.palette.text.primary,
          textUnderlineOffset: '2px',
        },
      })}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(notification.content) }}
    />
  );
};

export const notificationExtraContent: Record<string, React.ReactNode> = {
  'email-limit-reached': (
    <Box>
      <Typography variant="h6" sx={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 500 }}>
        To continue using email lookup, follow these steps:
      </Typography>
      <Box component="ol" sx={{ margin: 0, paddingLeft: '24px', listStyle: 'none', counterReset: 'step-counter' }}>
        <Box
          component="li"
          sx={{
            counterIncrement: 'step-counter',
            marginBottom: '20px',
            paddingLeft: '36px',
            position: 'relative',
            lineHeight: 1.6,
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '24px',
              height: '24px',
              backgroundColor: 'secondary.main',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            1
          </Box>
          Go to{' '}
          <Link
            href={`/settings/email-retrieval-api`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'secondary.main',
              textDecoration: 'none',
              fontWeight: 500,
              borderBottom: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': {
                color: 'secondary.dark',
                borderColor: 'secondary.dark',
              },
            }}
          >
            Settings › Outreach › ContactOut, Apollo, RocketReach API
          </Link>
        </Box>
        <Box
          component="li"
          sx={{
            counterIncrement: 'step-counter',
            marginBottom: '20px',
            paddingLeft: '36px',
            position: 'relative',
            lineHeight: 1.6,
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '24px',
              height: '24px',
              backgroundColor: 'secondary.main',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            2
          </Box>
          Select the API provider and enter the API key.
        </Box>
        <Box
          component="li"
          sx={{
            counterIncrement: 'step-counter',
            marginBottom: 0,
            paddingLeft: '36px',
            position: 'relative',
            lineHeight: 1.6,
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '24px',
              height: '24px',
              backgroundColor: 'secondary.main',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            3
          </Box>
          Go back to the extension and refresh the page and try again.
        </Box>
      </Box>
    </Box>
  ),
};

export const formatNotificationDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const applyReadState = (
  notifications: NotificationItem[],
  notificationId: string,
  viewedAt: string | undefined,
): NotificationItem[] =>
  notifications.map(notification => {
    if (notification._id !== notificationId) {
      return notification;
    }

    if (viewedAt) {
      return { ...notification, viewedAt };
    }

    const { viewedAt: _removed, ...rest } = notification;
    return rest;
  });

type Props = {
  notifications: NotificationItem[];
  onChange: (notifications: NotificationItem[], unreadDelta: number) => void;
  loading?: boolean;
  maxHeight?: number | string;
  onCloseExtra?: () => void;
};

const NotificationList = ({ notifications, onChange, loading, maxHeight, onCloseExtra }: Props) => {
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [showExtraContentModal, setShowExtraContentModal] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const markAsRead = async (notification: NotificationItem, event: React.MouseEvent) => {
    event.stopPropagation();
    if (notification.viewedAt || updatingId === notification._id) {
      return;
    }

    setUpdatingId(notification._id);
    try {
      await sendApiRequest({
        path: `/notifications/${notification._id}/view`,
        method: 'POST',
      });
      onChange(applyReadState(notifications, notification._id, new Date().toISOString()), -1);
    } catch (error) {
      notify(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const markAsUnread = async (notification: NotificationItem, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!notification.viewedAt || updatingId === notification._id) {
      return;
    }

    setUpdatingId(notification._id);
    try {
      await sendApiRequest({
        path: `/notifications/${notification._id}/unread`,
        method: 'POST',
      });
      onChange(applyReadState(notifications, notification._id, undefined), 1);
    } catch (error) {
      notify(error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.viewedAt) {
      try {
        await sendApiRequest({
          path: `/notifications/${notification._id}/view`,
          method: 'POST',
        });
        onChange(applyReadState(notifications, notification._id, new Date().toISOString()), -1);
      } catch (error) {
        notify(error);
      }
    }

    if (notificationExtraContent[notification.type]) {
      setSelectedNotification(notification);
      setShowExtraContentModal(true);
    } else {
      onCloseExtra?.();
    }
  };

  const handleCloseExtraContentModal = () => {
    setShowExtraContentModal(false);
    setSelectedNotification(null);
    onCloseExtra?.();
  };

  return (
    <>
      <Modal
        open={showExtraContentModal}
        onClose={handleCloseExtraContentModal}
        title={selectedNotification?.content || ''}
        size="sm"
        closeLabel="OK"
        renderContent={() => (selectedNotification ? notificationExtraContent[selectedNotification.type] : null)}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress color="secondary" size={20} />
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ py: 2, px: 1.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No notifications
          </Typography>
        </Box>
      ) : (
        <List sx={{ padding: 0, maxHeight, overflow: maxHeight ? 'auto' : undefined }}>
          {notifications.map((notification, index) => {
            const isWarning = notification.level === 'warning';
            const isUnread = !notification.viewedAt;
            const isUpdating = updatingId === notification._id;

            return (
              <React.Fragment key={notification._id}>
                <ListItem
                  disablePadding
                  sx={theme => ({
                    display: 'block',
                    backgroundColor: isUnread
                      ? isWarning
                        ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.1 : 0.08)
                        : 'action.hover'
                      : 'transparent',
                    borderLeft: isUnread || isWarning ? '3px solid' : 'none',
                    borderColor: isWarning ? 'warning.main' : isUnread ? 'primary.main' : 'transparent',
                  })}
                >
                  <ListItemButton
                    onClick={() => void handleNotificationClick(notification)}
                    sx={theme => ({
                      alignItems: 'flex-start',
                      py: 1,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: isWarning
                          ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.12)
                          : 'action.selected',
                      },
                    })}
                  >
                    <ListItemText
                      sx={{ m: 0 }}
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Typography
                          variant="body2"
                          component="div"
                          onClick={event => {
                            if ((event.target as HTMLElement).closest('a')) {
                              event.stopPropagation();
                            }
                          }}
                          sx={notificationContentSx(isUnread)}
                        >
                          {renderNotificationContent(notification)}
                        </Typography>
                      }
                      secondary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {formatNotificationDate(notification.createdAt)}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color={isUnread ? 'primary' : 'inherit'}
                            disabled={isUpdating}
                            onClick={event => {
                              if (isUnread) {
                                void markAsRead(notification, event);
                              } else {
                                void markAsUnread(notification, event);
                              }
                            }}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            {isUnread ? 'Read' : 'Unread'}
                          </Button>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </>
  );
};

export default NotificationList;
